import { useTranslation } from '@utils/i18n/i18n';

function matchesFormFactor(vehicleType, vehicleTypes) {
  return !vehicleTypes?.length || vehicleTypes.includes(vehicleType.form_factor);
}

function matchesPropulsion(vehicleType, propulsion) {
  if (!propulsion?.length) {
    return true;
  }

  const propulsionIds = propulsion.map(p => p.id);
  let propulsionType = vehicleType.propulsion_type;
  if (propulsionType === 'electric_assist') propulsionType = 'electric';
  if (propulsionType === 'plug_in_hybrid') propulsionType = 'hybrid';

  return propulsionIds.includes(propulsionType);
}

function matchesTransmission(vehicleType, transmission) {
  // No filter or no vehicle_accessories available -> show
  if (!transmission?.length || !vehicleType.vehicle_accessories) {
    return true;
  }

  return transmission.find(t => vehicleType.vehicle_accessories.includes(t.id))
}

function matchesProvider(provider, providers) {
  if (!providers?.length) {
    return true;
  }

  return providers.map(p => p.id).includes(provider);
}

function filterVehicleTypes(vehicles, vehicleTypes) {
  if (!vehicles) return [];

  return vehicles
    .filter(vehicle => matchesFormFactor(vehicle.properties.vehicleType, vehicleTypes));
}

function filterVehiclePropulsion(vehicles, propulsion) {
  if (!vehicles) return [];

  return vehicles.filter(vehicle => matchesPropulsion(vehicle.properties.vehicleType, propulsion));
}

function filterVehicleTransmission(vehicles, transmission) {
  if (!vehicles) return [];

  return vehicles
    .filter(vehicle => matchesTransmission(vehicle.properties.vehicleType, transmission));
}

function filterVehicleProvider(vehicles, providers) {
  if (!vehicles) return [];

  return vehicles.filter(vehicle => matchesProvider(vehicle.properties.operator, providers));
}

function filterStationVehicleTypes(stations, vehicleTypes) {
  if (!stations) {
    return [];
  }

  return stations.map(station => ({
    ...station,
    properties: {
      ...station.properties,
      vehicles: (station.properties.vehicles || [])
        .filter(vehicle => matchesFormFactor(vehicle.vehicleType, vehicleTypes)),
    },
  }));
}

function filterStationPropulsion(stations, propulsion) {
  if (!stations) return [];

  return stations.map(station => ({
    ...station,
    properties: {
      ...station.properties,
      vehicles: (station.properties.vehicles || [])
        .filter(vehicle => matchesPropulsion(vehicle.vehicleType, propulsion)),
    },
  }));
}

function filterStationTransmission(stations, transmission) {
  if (!stations) return [];

  return stations.map(station => ({
    ...station,
    properties: {
      ...station.properties,
      vehicles: (station.properties.vehicles || [])
        .filter(vehicle => matchesTransmission(vehicle.vehicleType, transmission)),
    },
  }));
}

function filterStationProvider(stations, providers) {
  if (!stations) return [];

  return stations.filter(station => matchesProvider(station.properties.operator, providers));
}

function isCargoBikeStation(station) {
  return station?.properties?.acceptable_bike_types?.includes('cargobike');
}

function hasAvailableCargoBikes(vehicles, stations) {
  return vehicles?.some(vehicle => (
    vehicle?.properties?.vehicleType?.form_factor === 'cargo_bicycle'
  )) || stations?.some(station => (
    station?.properties?.vehicles?.some(vehicle => (
      vehicle?.vehicleType?.form_factor === 'cargo_bicycle'
      && Number(vehicle.availability) > 0
    ))
  ));
}

function getMarkerCounterValue(station, vehicle) {
  if (!vehicle) {
    return 0;
  }

  const hasAvailability = vehicle?.availability && !Number.isNaN(vehicle?.availability);

  if (vehicle?.vehicleType?.form_factor === 'bicycle' && hasAvailability >= 0) {
    const countAvailability = (station.properties.vehicles || [])
      .map(v => v.availability ?? 0)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return countAvailability;
  }

  if (station.properties?.capacity >= 0) {
    return station.properties.capacity;
  }

  return false;
}

function prepStations(
  stations,
  showEmptyStations = false,
  vehicleTypes = [],
  hasAvailableCargoBike = false,
) {
  const hasCargoBikeFilter = vehicleTypes.includes('cargo_bicycle');

  return stations
    .map((station) => {
      const vehicle = station.properties.vehicles?.[0];

      const counter = getMarkerCounterValue(station, vehicle);
      const vehicleType = vehicle?.vehicleType ? { ...vehicle.vehicleType } : {};

      if (isCargoBikeStation(station)) {
        vehicleType.form_factor = 'cargo_bicycle';
      }

      return {
        ...station,
        properties: {
          ...station.properties,
          vehicleType,
          ...(counter === 0 || counter > 0 ? { marker_counter: counter } : {}),
        },
      }
    })
    .filter((station) => {
      if (station.properties.marker_counter !== 0) return true;

      return showEmptyStations
        && hasCargoBikeFilter
        && hasAvailableCargoBike
        && isCargoBikeStation(station);
    });
}

async function downloadData(
  vehicleTypes,
  providers,
  transmission,
  propulsion,
  commune,
) {
  const url = '/api/download?format=xlsx'
    + `&form_factor=${vehicleTypes.join(',')}`
    + `${providers.length ? `&operator=${providers.map(provider => provider.id).join(',')}` : ''}`
    + `${transmission.length ? `&transmission=${transmission.map(t => t.id).join(',')}` : ''}`
    + `${propulsion.length ? `&propulsion_type=${propulsion.map(p => p.id).join(',')}` : ''}`
    + `${commune?.id ? `&commune=${commune.id}` : ''}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${import.meta.env.VITE_NAZKA_WAY_TO_GO_TOKEN}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download: ${response.statusText}`);
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;

    // Optionally, set a default file name
    const contentDisposition = response.headers.get('Content-Disposition');
    const fileName = contentDisposition
      ? contentDisposition.split('filename=')[1]?.trim() || 'download'
      : 'download';

    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    // Clean up
    a.remove();
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading data:', error);
  }
}

function getAllVehicleTypes(vehicles, stations) {
  return [
    ...vehicles?.map(d => d.properties.vehicleType) || [],
    ...stations?.flatMap(s => s.properties.vehicles || []).map(d => d.vehicleType) || [],
  ];
}

function useFormFactorOptions() {
  const { t } = useTranslation();

  return [
    { id: 'car', label: t('filter_transport-type_car_tooltip') },
    { id: 'bicycle', label: t('filter_transport-type_bicycle_tooltip') },
    { id: 'cargo_bicycle', label: t('filter_transport-type_cargo_bicycle_tooltip') },
    { id: 'scooter', label: t('filter_transport-type_scooter_tooltip') },
    { id: 'moped', label: t('filter_transport-type_moped_tooltip') },
  ];
}

function useTransmissionOptions() {
  const { t } = useTranslation();

  return [
    { label: t('filter_transmission_input_manual'), id: 'manual' }, 
    { label: t('filter_transmission_input_automatic'), id: 'automatic' },
  ];
}

function usePropulsionOptions() {
  const { t } = useTranslation();

  return [
    { label: t('filter_propulsion_input_electric'), id: 'electric' }, 
    { label: t('filter_propulsion_input_hybrid'), id: 'hybrid' },
    { label: t('filter_propulsion_input_combustion'), id: 'combustion' }, 
    { label: t('filter_propulsion_input_combustion_diesel'), id: 'combustion_diesel' }, 
    { label: t('filter_propulsion_input_hydrogen_fuel_cell'), id: 'hydrogen_fuel_cell' }, 
    { label: t('filter_propulsion_input_human'), id: 'human' },
  ];
}

function usePropulsionNotUsedOptions() {
  const { t } = useTranslation();

  return [
    { label: t('popup_propulsion_electric-assist'), id: 'electric_assist' }, 
    { label: t('popup_propulsion_plug-in-hybrid'), id: 'plug_in_hybrid' },
  ];
}

export {
  filterVehicleTypes, filterVehiclePropulsion, filterVehicleTransmission, filterVehicleProvider,
  filterStationVehicleTypes, filterStationPropulsion, filterStationTransmission,
  filterStationProvider, isCargoBikeStation, hasAvailableCargoBikes, prepStations,
  downloadData, getAllVehicleTypes,
  usePropulsionOptions,
  usePropulsionNotUsedOptions, useTransmissionOptions, useFormFactorOptions,
};
